import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Briefcase, Check, ChevronDown, Search } from 'lucide-react';
import { Popover } from './linear/Popover';
import { useProjects } from '../../hooks/useProjects';
import { useGeneralProject } from '../../hooks/useTasks';
import { useClients } from '../../hooks/useClients';
import { ensureClientDefaultProject } from '../../lib/clientsApi';
import { clientColor, clientLabel } from '../../lib/clientLabel';
import type { ProjectStatus, ProjectSummary } from '../../lib/projectsApi';

// CLE-199: a task must belong to a project, so the picker only offers projects
// that can still receive work. Completed/cancelled/lead projects are hidden.
const ASSIGNABLE_STATUSES: ProjectStatus[] = [
  'ACTIVE',
  'PLANNING',
  'ALMOST_DONE',
  'ON_HOLD',
  'IN_HOUSE',
];

// Small colored dot mirroring the project status palette (ProjectBadges.tsx).
const STATUS_DOT: Record<ProjectStatus, string> = {
  PLANNING: 'bg-gray-400',
  ACTIVE: 'bg-brand-600',
  ON_HOLD: 'bg-amber-500',
  COMPLETED: 'bg-brand-600',
  CANCELLED: 'bg-red-500',
  ALMOST_DONE: 'bg-teal-500',
  LEAD: 'bg-purple-500',
  IN_HOUSE: 'bg-indigo-500',
};

function projectLabel(p: ProjectSummary): string {
  return p.code ? `${p.code} · ${p.name}` : p.name;
}

interface ProjectSelectProps {
  value: string;
  onChange: (projectId: string) => void;
  /** Compact trigger styling for inline use (quick-add rows). */
  compact?: boolean;
  /** Width of the dropdown panel in px. */
  width?: number;
  placeholder?: string;
  /** Render a dark trigger (used inside the Linear-style dark quick-add rows). */
  dark?: boolean;
  autoOpen?: boolean;
  onPanelClose?: () => void;
}

/**
 * CLE-199 — searchable project picker.
 *
 * Lists only assignable projects with a status dot + code + name, searchable by
 * BOTH name and code (typing "RTR" finds "RetuRO"). Recently-updated projects
 * are shown first, then the rest alphabetically. Reuses the dependency-free
 * Popover so it works inside modals and inline quick-add rows alike.
 */
export function ProjectSelect({
  value,
  onChange,
  compact = false,
  width = 280,
  placeholder = 'Selectează un proiect…',
  dark = false,
  autoOpen = false,
  onPanelClose,
}: ProjectSelectProps) {
  const { data, isLoading } = useProjects({ limit: 200 });
  // "Fără proiect": the company's container for tasks without a project. It is
  // not in the projects list (hidden from every project surface), so the
  // picker pins it on top as its own entry.
  const { data: general } = useGeneralProject();
  // Clients: filing a task "on the client" with no real project lands it in
  // the client's own container (created on first pick).
  const { data: clientsData } = useClients({ limit: 100, active: 'true', sort: 'name' });
  const qc = useQueryClient();
  const [query, setQuery] = useState('');

  const projects = useMemo(() => {
    const all = data?.projects ?? [];
    const assignable = all.filter((p) => ASSIGNABLE_STATUSES.includes(p.status));
    // Recent first (by updatedAt desc), then alphabetical by name as a stable
    // tiebreaker. We surface the 5 most-recent as a "Recent" group.
    return [...assignable].sort((a, b) => {
      const at = new Date(a.updatedAt).getTime();
      const bt = new Date(b.updatedAt).getTime();
      if (bt !== at) return bt - at;
      return a.name.localeCompare(b.name);
    });
  }, [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) || (p.code ? p.code.toLowerCase().includes(q) : false),
    );
  }, [projects, query]);

  const clients = clientsData?.data ?? [];
  const clientMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(
      (c) => c.name.toLowerCase().includes(q) || (c.shortName ?? '').toLowerCase().includes(q),
    );
  }, [clients, query]);
  const selectedClient = clients.find((c) => c.defaultProjectId && c.defaultProjectId === value);

  async function pickClient(clientId: string, defaultProjectId: string | null) {
    const id = defaultProjectId ?? (await ensureClientDefaultProject(clientId)).id;
    if (!defaultProjectId) void qc.invalidateQueries({ queryKey: ['clients'] });
    onChange(id);
  }

  const selected = projects.find((p) => p.id === value);
  const isGeneralSelected = !!general && value === general.id;
  const generalMatches =
    !!general && (!query.trim() || general.name.toLowerCase().includes(query.trim().toLowerCase()));

  const triggerClasses = dark
    ? 'flex items-center gap-1.5 rounded border border-gray-300 bg-gray-200 px-2 py-1 text-[13px] text-gray-800 hover:border-gray-400'
    : compact
      ? 'flex items-center gap-1.5 rounded border border-gray-300 px-2 py-1.5 text-sm text-gray-700 hover:border-gray-400'
      : 'ui-select mt-1 flex w-full items-center justify-between gap-2 text-left';

  return (
    <Popover
      width={width}
      onOpenChange={(open) => {
        if (open) setQuery('');
        else onPanelClose?.();
      }}
      trigger={({ toggle, ref }) => (
        <button
          type="button"
          ref={(el) => ref(el)}
          onClick={toggle}
          autoFocus={autoOpen}
          className={triggerClasses}
        >
          {selected ? (
            <span className="flex min-w-0 items-center gap-2">
              <span
                className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${STATUS_DOT[selected.status]}`}
              />
              <span className="truncate">{projectLabel(selected)}</span>
            </span>
          ) : selectedClient ? (
            <span className="flex min-w-0 items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: clientColor(selectedClient) }}
              />
              <span className="truncate">{clientLabel(selectedClient)} · fără proiect</span>
            </span>
          ) : isGeneralSelected ? (
            <span className="flex min-w-0 items-center gap-2">
              <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border border-dashed border-gray-400" />
              <span className="truncate">{general.name}</span>
            </span>
          ) : (
            <span className={dark ? 'text-gray-400' : 'text-gray-400'}>{placeholder}</span>
          )}
          <ChevronDown size={14} className="ml-auto shrink-0 opacity-60" />
        </button>
      )}
    >
      {(close) => (
        <div className="flex max-h-72 flex-col">
          <div className="flex items-center gap-2 border-b border-gray-200 px-2.5 py-2">
            <Search size={14} className="pointer-events-none shrink-0 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Caută după nume sau cod…"
              className="w-full bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
            />
          </div>
          <div className="overflow-y-auto py-1">
            {isLoading && <p className="px-3 py-2 text-sm text-gray-400">Se încarcă proiectele…</p>}
            {generalMatches && (
              <button
                type="button"
                onClick={() => {
                  onChange(general.id);
                  close();
                }}
                className="ui-menu-item justify-between"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border border-dashed border-gray-400" />
                  <span className="truncate">{general.name}</span>
                </span>
                {isGeneralSelected && <Check size={14} className="shrink-0 text-gray-400" />}
              </button>
            )}
            {!isLoading &&
              filtered.length === 0 &&
              !generalMatches &&
              clientMatches.length === 0 && (
                <p className="px-3 py-2 text-sm text-gray-400">Niciun proiect găsit.</p>
              )}
            {filtered.map((p) => {
              const isOn = p.id === value;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    onChange(p.id);
                    close();
                  }}
                  className="ui-menu-item justify-between"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${STATUS_DOT[p.status]}`}
                    />
                    {p.code && (
                      <span className="shrink-0 font-mono text-xs text-gray-400">{p.code}</span>
                    )}
                    <span className="truncate">{p.name}</span>
                  </span>
                  {isOn && <Check size={14} className="shrink-0 text-gray-400" />}
                </button>
              );
            })}
            {clientMatches.length > 0 && (
              <p className="mt-1 flex items-center gap-1.5 border-t border-gray-100 px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                <Briefcase size={12} /> Pe client, fără proiect
              </p>
            )}
            {clientMatches.map((c) => {
              const isOn = !!c.defaultProjectId && c.defaultProjectId === value;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    void pickClient(c.id, c.defaultProjectId);
                    close();
                  }}
                  className="ui-menu-item justify-between"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: clientColor(c) }}
                    />
                    <span className="truncate">{clientLabel(c)}</span>
                  </span>
                  {isOn && <Check size={14} className="shrink-0 text-gray-400" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </Popover>
  );
}
